import { useEffect, useState } from 'preact/hooks';
import { Button } from '../../components/ui/Button.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { API_BASE_URL, VAPID_PUBLIC_KEY } from '../../config.js';
// 138: these two were private to this file until the install offer needed the same rules
// (research.md R3). Behaviour is unchanged — this file's own tests were not touched.
import { IOS_PUSH_INSTALL_HINT, isIOS, isStandaloneDisplay } from '../../pwa/platform.js';
import {
  needsRepair,
  repairSubscription,
  subscribeAndRegister,
} from '../../pwa/pushRegistration.js';

interface PushNotificationsSettingsProps {
  path?: string;
}

export function PushNotificationsSettings(_props: PushNotificationsSettingsProps) {
  const [status, setStatus] = useState<'idle' | 'requesting' | 'subscribed' | 'error'>('idle');
  const [message, setMessage] = useState('');

  // 206 (#285, US3/FR-009, research.md R5): real state on mount, no click required. Cheap local
  // check first (the common case — a customer who never opted in — costs no network call); only a
  // truthy local subscription triggers the backend read, since that is the only case where the two
  // could possibly disagree.
  useEffect(() => {
    let cancelled = false;

    async function checkSubscribed() {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
      try {
        const registration = await navigator.serviceWorker.ready;
        const existing = await registration.pushManager.getSubscription();
        if (!existing || cancelled) return;

        const res = await fetch(
          `${API_BASE_URL}/push-subscriptions?endpoint=${encodeURIComponent(existing.endpoint)}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('access_token') ?? ''}` } },
        );
        if (!res.ok || cancelled) return;
        const body = (await res.json()) as { subscribed?: boolean };
        if (!cancelled && body.subscribed) setStatus('subscribed');
      } catch {
        // Left at the default 'idle' — a customer who is in fact still subscribed sees "Enable"
        // once and re-registers, which the existing POST route's upsert makes harmless.
      }
    }

    void checkSubscribed();
    return () => {
      cancelled = true;
    };
  }, []);

  async function subscribe() {
    if (isIOS() && !isStandaloneDisplay()) {
      setMessage(IOS_PUSH_INSTALL_HINT);
      setStatus('error');
      return;
    }

    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setMessage('Push notifications are not supported in this browser.');
      setStatus('error');
      return;
    }

    setStatus('requesting');
    setMessage('');

    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setMessage('Notification permission denied.');
        setStatus('error');
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      const existing = await registration.pushManager.getSubscription();
      const deps = {
        apiBaseUrl: API_BASE_URL,
        accessToken: localStorage.getItem('access_token') ?? '',
      };

      // 197 (contracts/push-repair.md): unlike the silent path, this one may act on
      // `indeterminate` — the customer asked, and the worst case is an identical working
      // registration, never a destroyed one with nothing to replace it (research.md §2).
      const result = needsRepair(
        existing ? { applicationServerKey: existing.options?.applicationServerKey } : null,
        VAPID_PUBLIC_KEY,
      );

      if (result === 'superseded' || result === 'indeterminate') {
        await repairSubscription(
          existing as PushSubscription,
          registration,
          VAPID_PUBLIC_KEY,
          deps,
        );
      } else if (result === 'no-registration') {
        await subscribeAndRegister(registration, VAPID_PUBLIC_KEY, deps);
      }
      // 'current': already registered under the published key — nothing to do.

      setStatus('subscribed');
      setMessage('Push notifications enabled!');
    } catch (err) {
      setMessage((err as { message?: string }).message ?? 'Failed to enable push notifications');
      setStatus('error');
    }
  }

  async function unsubscribe() {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) return;

      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();

      await fetch(`${API_BASE_URL}/push-subscriptions`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token') ?? ''}`,
        },
        body: JSON.stringify({ endpoint }),
      });

      setStatus('idle');
      setMessage('Push notifications disabled.');
    } catch (err) {
      setMessage((err as { message?: string }).message ?? 'Failed to unsubscribe');
      setStatus('error');
    }
  }

  return (
    <div class="p-4">
      <PageHeader title="Push notifications" fallback="/account" />
      <h2 class="text-lg font-bold text-text mb-2">Push Notifications</h2>
      <p class="text-gray-400 text-sm mb-4">
        Receive real-time updates about your bookings directly on this device.
      </p>

      {status === 'subscribed' ? (
        <Button variant="secondary" onClick={unsubscribe}>
          Disable push notifications
        </Button>
      ) : (
        <Button loading={status === 'requesting'} onClick={subscribe}>
          Enable push notifications
        </Button>
      )}

      {message && (
        <p class={`mt-3 text-sm ${status === 'error' ? 'text-red-400' : 'text-green-400'}`}>
          {message}
        </p>
      )}
    </div>
  );
}
